import { TextSegmentData, AudioSettings } from '../contexts/ScriptCreationContext';

/**
 * Default pause time for a punctuation mark, based on the user's audio settings.
 */
export function getPauseTimeForPunctuation(punctuation: string, advancedSettings: AudioSettings): string {
  switch (punctuation) {
    case ',': return `${advancedSettings.commaPause}s`;
    case '.': case '!': case '?': return `${advancedSettings.periodPause}s`;
    case ':': case ';': return `${advancedSettings.colonPause}s`;
    case '-': return `${advancedSettings.dashPause}s`;
    case '\n': return `${advancedSettings.paragraphPause}s`;
    default: return `${advancedSettings.commaPause}s`;
  }
}

/**
 * Parse raw meditation text into text + pause segments.
 *
 * Extracted verbatim from Speech.tsx so the Zen flow and the Speech page produce
 * identical segmentation. Supports:
 * - 'sentence' mode (split on . ! ?) and 'punctuation' mode (also , : ; -)
 * - [N] / [Ns] / [Nm] pause notation (inline or as a standalone paragraph)
 * - paragraph breaks -> paragraphPause
 * - guarantees the part ends with a paragraph-level pause
 */
export function parseTextToSegments(
  text: string,
  partId: string,
  mode: 'punctuation' | 'sentence' | 'paragraph',
  advancedSettings: AudioSettings
): TextSegmentData[] {
  const segments: TextSegmentData[] = [];
  let currentId = 0;

  // Clean up text by normalizing multiple newlines and whitespace
  const cleanedText = text
    .replace(/\u00A0/g, ' ')     // Replace non-breaking spaces with regular spaces
    .replace(/\u200B/g, '')      // Remove zero-width spaces
    .replace(/\n{3,}/g, '\n\n')  // Replace 3+ newlines with just 2
    .replace(/[ \t]+\n/g, '\n')  // Remove trailing spaces before newlines
    .replace(/\n[ \t]+/g, '\n')  // Remove leading spaces after newlines
    .replace(/\n\s*\n/g, '\n\n') // Normalize paragraph breaks
    .trim();                     // Remove leading/trailing whitespace

  const paragraphs = cleanedText.split(/\n+/);

  for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
    const paragraph = paragraphs[paragraphIndex].trim();

    if (paragraph) {
      // Check if this entire paragraph is just a pause notation [numbers] or [numberm]
      const standalonePauseMatch = paragraph.match(/^\[(\d*\.?\d+)([sm])?\]$/);
      if (standalonePauseMatch) {
        const value = parseFloat(standalonePauseMatch[1]);
        const unit = standalonePauseMatch[2] || 's';
        const pauseTime = unit === 'm' ? `${value * 60}s` : `${value}s`;
        segments.push({
          id: `${partId}-segment-${currentId++}`,
          type: 'pause',
          content: paragraph, // Store original format for paragraph breaks
          pauseTime: pauseTime,
          isManuallyModified: true,
          isParagraphBreak: true
        });
      } else {
        // Split based on segmentation mode
        let parts: string[];
        if (mode === 'sentence') {
          parts = paragraph.split(/(\s*[.!?]\s*|\[\d*\.?\d+[sm]?\])/);
        } else {
          parts = paragraph.split(/(\s*[.!?,:;-]\s*|\[\d*\.?\d+[sm]?\])/);
        }

        for (let i = 0; i < parts.length; i++) {
          const part = parts[i].trim();
          if (!part) continue;

          const pauseMatch = part.match(/^\[(\d*\.?\d+)([sm])?\]$/);
          if (pauseMatch) {
            const value = parseFloat(pauseMatch[1]);
            const unit = pauseMatch[2] || 's';
            const pauseTime = unit === 'm' ? `${value * 60}s` : `${value}s`;
            segments.push({
              id: `${partId}-segment-${currentId++}`,
              type: 'pause',
              content: part, // Store the full [number] notation
              pauseTime: pauseTime,
              isManuallyModified: true,
              isParagraphBreak: false
            });
          } else if (mode === 'sentence' ? /[.!?]/.test(part) : /[.!?,:;-]/.test(part)) {
            const punctuationMatch = mode === 'sentence'
              ? part.match(/[.!?]/)
              : part.match(/[.!?,:;-]/);
            const punctuationMark = punctuationMatch ? punctuationMatch[0] : part;
            const pauseTime = getPauseTimeForPunctuation(punctuationMark, advancedSettings);
            segments.push({
              id: `${partId}-segment-${currentId++}`,
              type: 'pause',
              content: punctuationMark, // Store just the punctuation mark
              pauseTime: pauseTime,
              isManuallyModified: false,
              isParagraphBreak: false
            });
          } else {
            segments.push({
              id: `${partId}-segment-${currentId++}`,
              type: 'text',
              content: part
            });
          }
        }
      }
    }

    // Add paragraph break after each paragraph except the last one (only if not a standalone pause)
    if (paragraphIndex < paragraphs.length - 1) {
      const isStandalonePause = paragraph.trim().match(/^\[(\d*\.?\d+)([sm])?\]$/);
      const nextParagraph = paragraphs[paragraphIndex + 1]?.trim();
      const nextIsStandalonePause = nextParagraph?.match(/^\[(\d*\.?\d+)([sm])?\]$/);

      if (!isStandalonePause && !nextIsStandalonePause) {
        segments.push({
          id: `${partId}-segment-${currentId++}`,
          type: 'pause',
          content: '\n',
          pauseTime: `${advancedSettings.paragraphPause}s`,
          isManuallyModified: false,
          isParagraphBreak: true
        });
      }
    }
  }

  // Ensure each part ends with a paragraph pause unless it already does
  if (segments.length > 0) {
    const lastSegment = segments[segments.length - 1];

    const isLastSegmentParagraphPause = lastSegment.type === 'pause' &&
      (lastSegment.isParagraphBreak ||
       lastSegment.content === '\n' ||
       lastSegment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/) ||
       lastSegment.isManuallyModified);

    if (!isLastSegmentParagraphPause) {
      segments.push({
        id: `${partId}-segment-${currentId++}`,
        type: 'pause',
        content: '\n',
        pauseTime: `${advancedSettings.paragraphPause}s`,
        isManuallyModified: false,
        isParagraphBreak: true
      });
    }
  }

  return segments;
}
